/**
 * ProductGallery Component
 * Ported from afrodita-components - Simplified for template-project
 * Desktop gallery with vertical thumbnails and main image display
 */

import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import styled from '@emotion/styled';
import InnerImageZoom from 'react-inner-image-zoom';
import 'react-inner-image-zoom/lib/styles.min.css';

export interface ProductGalleryImage {
  url: string;
  key: string;
  alt?: string;
  isVideo?: boolean;
  posterUrl?: string;
  mimeType?: string;
  zoomUrl?: string;
}

export interface ZoomConfig {
  enabled?: boolean;
  zoomType?: 'hover' | 'click';
  hideHint?: boolean;
  zoomScale?: number;
}

export interface ProductGalleryProps {
  images: ProductGalleryImage[];
  selectedValue?: ProductGalleryImage;
  topTag?: React.ReactNode;
  topRightTag?: React.ReactNode;
  bottomTag?: React.ReactNode;
  thumbnailPosition?: 'vertical' | 'horizontal';
  hasToPreserveOrder?: boolean;
  zoomConfig?: ZoomConfig;
}

const GalleryContainer = styled.div<{ thumbnailPosition: 'vertical' | 'horizontal'; showThumbnails: boolean }>`
  display: grid;
  grid-template-columns: ${({ thumbnailPosition, showThumbnails }) =>
    thumbnailPosition === 'horizontal' || !showThumbnails ? '1fr' : '80px 1fr'};
  grid-column-gap: 12px;
  width: 100%;
  max-width: 670px;
  /* Let the 1fr column shrink so inline-block .iiz still gets a bounded width (grid default min-width: auto). */
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    max-width: none;
  }
`;

const ThumbnailList = styled.div<{ thumbnailPosition: 'vertical' | 'horizontal' }>`
  display: flex;
  flex-direction: ${({ thumbnailPosition }) => (thumbnailPosition === 'horizontal' ? 'row' : 'column')};
  gap: ${({ theme }) => theme.space.sm};
  overflow-y: ${({ thumbnailPosition }) => (thumbnailPosition === 'vertical' ? 'auto' : 'visible')};
  overflow-x: ${({ thumbnailPosition }) => (thumbnailPosition === 'horizontal' ? 'auto' : 'visible')};
  max-height: ${({ thumbnailPosition }) => (thumbnailPosition === 'vertical' ? '100%' : 'auto')};
  padding: ${({ theme }) => `${theme.space.xs} 0`};

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => `${theme.colors.border.color} transparent`};

  &::-webkit-scrollbar {
    width: ${({ theme }) => theme.space.xs};
    height: ${({ theme }) => theme.space.xs};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.border.color};
    border-radius: ${({ theme }) => theme.radius.small};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: visible;
    order: 2;
    padding: ${({ theme }) => `${theme.space.sm} 0`};
  }
`;

const ThumbnailButton = styled.button<{ isActive: boolean }>`
  border-width: ${({ isActive }) => (isActive ? '2px' : '1px')};
  border-style: solid;
  border-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.text.primary : theme.colors.border.color};
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  background: transparent;
  transition: all 0.2s;
  flex-shrink: 0;
  width: 64px;
  height: 80px;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.secondary};
  }

  &:focus-visible {
    outline-width: ${({ theme }) => theme.space.xs};
    outline-style: solid;
    outline-color: ${({ theme }) => theme.colors.primary.color};
    outline-offset: ${({ theme }) => theme.space.xs};
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 48px;
    height: 60px;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const ThumbnailPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  
  svg {
    width: 16px;
    height: 16px;
    opacity: 0.5;
  }
`;

const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.background.secondary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    order: 1;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.tertiary};
  
  svg {
    width: 64px;
    height: 64px;
    opacity: 0.5;
  }
`;

const TagContainer = styled.div<{ position: 'top' | 'topRight' | 'bottom' }>`
  position: absolute;
  z-index: 2;

  ${({ position, theme }) => {
    switch (position) {
      case 'top':
        return `
          top: ${theme.space.md};
          left: ${theme.space.md};
        `;
      case 'topRight':
        return `
          top: ${theme.space.md};
          right: ${theme.space.md};
        `;
      case 'bottom':
        return `
          bottom: ${theme.space.md};
          left: ${theme.space.md};
        `;
    }
  }}
`;

const StyledInnerImageZoom = styled(InnerImageZoom)`
  max-width: 100%;
`;

const ImageZoomContainer = styled.div`
  .iiz {
    max-width: 100%;
    margin: 0;
    position: relative;
    overflow: hidden;
    display: inline-block;
    cursor: zoom-in;
  }

  .iiz.zoomWrapper {
    vertical-align: top;
  }

  .iiz--drag .iiz__zoom-img--visible {
    cursor: grab;
  }

  .iiz__img {
    max-width: 100%;
    height: auto;
    display: block;
    pointer-events: none;
    visibility: visible;
    opacity: 1;
  }

  .iiz__img--hidden {
    visibility: hidden;
    opacity: 0;
  }

  .iiz__img--abs {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: block;
  }

  .iiz__zoom-img {
    width: auto !important;
    max-width: none !important;
    position: absolute;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    display: block;
  }

  .iiz__zoom-img--visible {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
    cursor: zoom-out;
    touch-action: none;
    z-index: 10;
  }

  .iiz__zoom-portal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 99999;
    background: rgba(0, 0, 0, 1);
    overflow: hidden;
    touch-action: none;
  }

  .iiz__zoom-portal .iiz__zoom-img {
    max-width: none !important;
    max-height: none !important;
    cursor: grab !important;
    user-select: none !important;
    pointer-events: auto !important;
  }

  .iiz__zoom-portal .iiz__zoom-img--visible {
    visibility: visible !important;
    opacity: 1 !important;
    cursor: grab !important;
    pointer-events: auto !important;
    touch-action: none !important;
  }

  .iiz__zoom-portal .iiz__zoom-img--visible:active {
    cursor: grabbing !important;
  }

  .iiz__btn {
    background: rgba(255, 255, 255, 0.8);
    max-width: 95px;
    min-width: fit-content;
    height: 32px;
    border: none;
    padding: 0 8px;
    position: absolute;
    z-index: 5;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
  }

  .iiz__btn:before {
    content: ' ';
    background-position: center;
    background-repeat: no-repeat;
    display: block;
  }

  .iiz__hint {
    bottom: 10%;
    right: 0;
    pointer-events: none;
    border-radius: 150px 0 0 150px;
  }

  .iiz__hint:before {
    content: '+';
    background-image: none;
    width: 17px;
    height: 17px;
    font-size: 19px;
    border-radius: 500px;
    border: 2px solid #292929;
    display: inline-block;
    position: relative;
    text-align: center;
    line-height: 17px;
    font-weight: 600;
  }

  .iiz__hint:after {
    content: 'Zoom in';
    font-size: 14px;
    font-weight: 600;
    display: inline-block;
    margin-left: 5px;
  }

  .iiz__close {
    border-radius: 25px;
    position: fixed !important;
    top: auto !important;
    right: auto !important;
    left: 50% !important;
    bottom: 40px !important;
    transform: translateX(-50%) !important;
    margin: 0 !important;
    visibility: hidden;
    opacity: 0;
    color: #292929;
    background: rgba(255, 255, 255, 0.95) !important;
    padding: 12px 20px !important;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    backdrop-filter: blur(10px) !important;
    z-index: 100001 !important;
  }

  .iiz__close--visible {
    visibility: visible;
    opacity: 1;
    cursor: pointer;
  }

  .iiz__close::before {
    content: ' ';
    width: 16px;
    height: 16px;
    background-image: linear-gradient(#222, #222), linear-gradient(#222, #222);
    background-size: 65% 2px, 2px 65%;
    transform: rotate(45deg);
    border-radius: 500px;
    border: 2px solid #292929;
  }

  .iiz__close::after {
    content: 'Close';
    font-size: 14px;
    font-weight: 600;
    display: inline-block;
    margin-left: 5px;
  }

  @media only screen and (max-width: 768px) {
    .iiz.zoomWrapper > div {
      height: auto;
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
`;

// Placeholder icon component for missing images
const PlaceholderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ThumbnailVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  pointer-events: none;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  object-fit: cover;
  object-position: center;
  display: block;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  aspect-ratio: 4/5;
  border: 0;
  display: block;
`;

const YOUTUBE_REGEX = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
const VIMEO_REGEX = /(?:vimeo\.com\/)(\d+)/;

function isExternalVideoUrl(url: string): boolean {
  return YOUTUBE_REGEX.test(url) || VIMEO_REGEX.test(url);
}

function getEmbedUrl(url: string): string | null {
  const ytMatch = YOUTUBE_REGEX.exec(url);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1`;

  const vimeoMatch = VIMEO_REGEX.exec(url);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1`;

  return null;
}

interface LazyVideoProps {
  src: string;
  poster?: string;
  isActive: boolean;
  shouldLoad: boolean;
}

const LazyVideo = memo(({ src, poster, isActive, shouldLoad }: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      video.play().catch(() => {
        // Autoplay may be blocked by browser policy
      });
    } else {
      video.pause();
    }
  }, [isActive]);

  return (
    <StyledVideo
      ref={videoRef}
      src={shouldLoad ? src : undefined}
      poster={poster}
      muted
      loop
      playsInline
      preload={shouldLoad ? 'metadata' : 'none'}
    />
  );
});

LazyVideo.displayName = 'LazyVideo';

const MOBILE_BREAKPOINT = 768;
const MOBILE_ZOOM_SCALE = 1.6;
const DESKTOP_ZOOM_SCALE = 0.8;

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = globalThis.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

function usePreloadZoomImages(images: ProductGalleryImage[], currentIndex: number) {
  const preloadedUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const indicesToPreload = [currentIndex, currentIndex - 1, currentIndex + 1];
      indicesToPreload.forEach(idx => {
        const img = images[idx];
        if (img?.zoomUrl && !img.isVideo && !preloadedUrls.current.has(img.zoomUrl)) {
          const preloadImg = new Image();
          preloadImg.src = img.zoomUrl;
          preloadedUrls.current.add(img.zoomUrl);
        }
      });
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [images, currentIndex]);
}

function renderThumbnailContent(
  image: ProductGalleryImage,
  index: number,
  hasFailed: boolean,
  onError: (key: string) => void,
) {
  if (hasFailed) {
    return (
      <ThumbnailPlaceholder>
        <PlaceholderIcon />
      </ThumbnailPlaceholder>
    );
  }

  if (image.isVideo) {
    return (
      <ThumbnailVideo
        src={image.url}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  }

  return (
    <ThumbnailImage
      src={image.url}
      alt={image.alt || `Product thumbnail ${index + 1}`}
      loading={index < 4 ? 'eager' : 'lazy'}
      onError={() => onError(image.key)}
    />
  );
}

function renderMainContent(
  activeImage: ProductGalleryImage | undefined,
  hasFailed: boolean,
  onError: (key: string) => void,
  zoomConfig?: ZoomConfig,
  isMobile?: boolean,
) {
  if (!activeImage) {
    return (
      <ImagePlaceholder>
        <PlaceholderIcon />
      </ImagePlaceholder>
    );
  }

  if (activeImage.isVideo) {
    if (isExternalVideoUrl(activeImage.url)) {
      return (
        <StyledIframe
          src={getEmbedUrl(activeImage.url) || activeImage.url}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          title={activeImage.alt || 'Product video'}
          loading="lazy"
        />
      );
    }
    return (
      <LazyVideo
        src={activeImage.url}
        poster={activeImage.posterUrl}
        isActive={true}
        shouldLoad={true}
      />
    );
  }

  if (hasFailed) {
    return (
      <ImagePlaceholder>
        <PlaceholderIcon />
      </ImagePlaceholder>
    );
  }

  if (zoomConfig?.enabled) {
    return (
      <StyledInnerImageZoom
        src={activeImage.url}
        zoomSrc={activeImage.zoomUrl || activeImage.url}
        zoomType={isMobile ? 'click' : (zoomConfig.zoomType || 'hover')}
        hideHint={zoomConfig.hideHint}
        zoomScale={isMobile ? MOBILE_ZOOM_SCALE : (zoomConfig.zoomScale || DESKTOP_ZOOM_SCALE)}
        fullscreenOnMobile={isMobile}
        className="zoomWrapper"
        imgAttributes={{
          alt: activeImage.alt || 'Product image',
          style: { objectFit: 'cover' },
        }}
      />
    );
  }

  return (
    <MainImage
      src={activeImage.url}
      alt={activeImage.alt || 'Product image'}
      loading="eager"
      onError={() => onError(activeImage.key)}
    />
  );
}

export function ProductGallery({
  images,
  selectedValue,
  topTag,
  topRightTag,
  bottomTag,
  thumbnailPosition = 'vertical',
  hasToPreserveOrder = false,
  zoomConfig,
}: ProductGalleryProps) {
  const isMobile = useIsMobile();
  // Track which images have failed to load
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Filter out images with empty/invalid URLs upfront
  const validImages = useMemo(() => {
    return images.filter(img => {
      // Check if URL exists and is not empty
      if (!img.url || typeof img.url !== 'string') return false;
      const trimmedUrl = img.url.trim();
      if (!trimmedUrl) return false;
      // Check if it's not just a placeholder or invalid URL
      if (trimmedUrl === '#' || trimmedUrl === 'undefined' || trimmedUrl === 'null') return false;
      return true;
    });
  }, [images]);

  const getInitialIndex = useCallback(() => {
    if (selectedValue && validImages.length > 0) {
      const index = validImages.findIndex(img => img.key === selectedValue.key);
      return index > -1 ? index : 0;
    }
    return 0;
  }, [selectedValue, validImages]);

  const [activeIndex, setActiveIndex] = useState(getInitialIndex());

  useEffect(() => {
    if (selectedValue && !hasToPreserveOrder && validImages.length > 0) {
      const index = validImages.findIndex(img => img.key === selectedValue.key);
      if (index > -1) {
        setActiveIndex(index);
      }
    }
  }, [selectedValue, hasToPreserveOrder, validImages]);

  // Reset failed images when images prop changes
  useEffect(() => {
    setFailedImages(new Set());
  }, [images]);

  // Handle image load error
  const handleImageError = useCallback((imageKey: string) => {
    setFailedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageKey);
      return newSet;
    });
  }, []);

  // Ensure activeIndex is within bounds
  const safeActiveIndex = Math.min(activeIndex, Math.max(validImages.length - 1, 0));
  const activeImage = validImages[safeActiveIndex];
  const activeImageFailed = activeImage ? failedImages.has(activeImage.key) : false;

  usePreloadZoomImages(validImages, safeActiveIndex);

  // Return null if no valid images
  if (!validImages || validImages.length === 0) {
    return null;
  }

  // Check if we have any non-failed images to show thumbnails
  const hasValidThumbnails = validImages.some(img => !failedImages.has(img.key));
  const showThumbnails = validImages.length > 1 && hasValidThumbnails;

  const galleryContent = (
    <GalleryContainer thumbnailPosition={thumbnailPosition} showThumbnails={showThumbnails}>
      {/* Thumbnails - only show if we have multiple images or at least one valid thumbnail */}
      {showThumbnails && (
        <ThumbnailList
          thumbnailPosition={thumbnailPosition}
          role="list"
          aria-label="Product images"
        >
          {validImages.map((image, index) => {
            const hasFailed = failedImages.has(image.key);

            return (
              <ThumbnailButton
                key={image.key}
                isActive={safeActiveIndex === index}
                onClick={() => setActiveIndex(index)}
                aria-label={image.isVideo ? `Play video ${index + 1}` : `View image ${index + 1}`}
                aria-current={safeActiveIndex === index}
                role="listitem"
              >
                {renderThumbnailContent(image, index, hasFailed, handleImageError)}
              </ThumbnailButton>
            );
          })}
        </ThumbnailList>
      )}

      {/* Main Image or Video */}
      <MainImageContainer>
        {renderMainContent(activeImage, activeImageFailed, handleImageError, zoomConfig, isMobile)}

        {/* Tags */}
        {topTag && <TagContainer position="top">{topTag}</TagContainer>}
        {topRightTag && <TagContainer position="topRight">{topRightTag}</TagContainer>}
        {bottomTag && <TagContainer position="bottom">{bottomTag}</TagContainer>}
      </MainImageContainer>
    </GalleryContainer>
  );

  if (zoomConfig?.enabled) {
    return <ImageZoomContainer>{galleryContent}</ImageZoomContainer>;
  }

  return galleryContent;
}
